/**
 * File type detection utilities for preview system
 */

export interface FileTypeInfo {
  extension: string;
  mimeType: string;
  category: 'csv' | 'image' | 'video' | 'audio' | 'text' | 'pdf' | 'other';
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(lastDot).toLowerCase() : '';
}

/**
 * Get MIME type from file extension
 */
export function getMimeTypeFromExtension(extension: string): string {
  const mimeTypeMap: Record<string, string> = {
    '.csv': 'text/csv',
    '.txt': 'text/plain',
    '.json': 'application/json',
    '.xml': 'application/xml',
    '.html': 'text/html',
    '.css': 'text/css',
    '.js': 'text/javascript',
    '.ts': 'text/typescript',
    '.md': 'text/markdown',
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.svg': 'image/svg+xml',
    '.webp': 'image/webp',
    '.mp4': 'video/mp4',
    '.webm': 'video/webm',
    '.avi': 'video/avi',
    '.mov': 'video/quicktime',
    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.ogg': 'audio/ogg',
    '.flac': 'audio/flac',
  };

  return mimeTypeMap[extension] || 'application/octet-stream';
}

/**
 * Get file category from extension or MIME type
 */
export function getFileCategory(
  filename: string,
  mimeType?: string
): FileTypeInfo['category'] {
  const extension = getFileExtension(filename);
  const mime = mimeType || getMimeTypeFromExtension(extension);

  if (extension === '.csv' || mime === 'text/csv') return 'csv';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (
    mime.startsWith('text/') ||
    mime === 'application/json' ||
    mime === 'application/xml'
  )
    return 'text';
  if (mime === 'application/pdf') return 'pdf';

  return 'other';
}

/**
 * Get complete file type information
 */
export function getFileTypeInfo(
  filename: string,
  mimeType?: string
): FileTypeInfo {
  const extension = getFileExtension(filename);
  const detectedMimeType = mimeType || getMimeTypeFromExtension(extension);
  const category = getFileCategory(filename, detectedMimeType);

  return {
    extension,
    mimeType: detectedMimeType,
    category,
  };
}

/**
 * Check if file type is supported for preview
 */
export function isPreviewSupported(
  filename: string,
  mimeType?: string
): boolean {
  const category = getFileCategory(filename, mimeType);

  // Currently supported categories
  const supportedCategories: FileTypeInfo['category'][] = [
    'csv',
    'image',
    'video',
    'audio',
    'text',
  ];

  return supportedCategories.includes(category);
}

/**
 * Get human-readable file type name
 */
export function getFileTypeName(filename: string, mimeType?: string): string {
  const { category, extension } = getFileTypeInfo(filename, mimeType);

  const categoryNames: Record<FileTypeInfo['category'], string> = {
    csv: 'CSV File',
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    text: 'Text File',
    pdf: 'PDF Document',
    other: 'File',
  };

  const baseName = categoryNames[category];

  if (extension) {
    return `${baseName} (${extension.toUpperCase()})`;
  }

  return baseName;
}
